import React, { useEffect, useState, useMemo } from 'react';
import { buildSearchEngine } from '@coveo/headless';
import { config } from '../../engine';
import {
  ResultActionAnalytics,
  ResultActionAnalyticsProvider
} from '../../context/ResultActionAnalyticsContext';
import DocumentTabListing from './DocumentTabListing';
import { buildResultTemplatesManager } from '@coveo/headless';
import DocumentListFile from '../../components/document-list-file';
import { InteractiveResultWithActionLink } from '../../components/interactive-result-with-action';

function DocumentTabListingContext(props) {
  const resultActionAnalytics = useMemo(() => new ResultActionAnalytics(), []);

  const buildEngine = options => {
    const engine = buildSearchEngine({
      configuration: {
        ...config,
        analytics: {
          analyticsClientMiddleware: options.analyticsClientMiddleware
        }
      }
    });

    return engine;
  };

  const engine = useMemo(() => {
    return buildEngine({
      analyticsClientMiddleware: resultActionAnalytics.getAnalyticsMiddleware()
    });
  }, [resultActionAnalytics]);

  const { dataset } = document.getElementsByClassName(
    'coveo-search-results__main-wrapper'
  )[0];

  const localization = JSON.parse(dataset.localization);

  // this function is used to convert unicode to string
  function covertUnicode(str) {
    if (typeof str === 'string') {
      return str.replace(/\\u[\dA-F]{4}/gi, match =>
        String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
      );
    }
    return str;
  }

  const resultTemplatesManager = buildResultTemplatesManager(engine);

  resultTemplatesManager.registerTemplates({
    conditions: [],
    fields: [
      'uniqueId',
      'documentname',
      'documentpdfurl',
      'documenttechnology',
      'documentdwgurl',
      'documentrvturl',
      'documentdocurl'
    ],
    content: result => (
      <li className="document-list__item" key={result.uniqueId}>
        <div className="document-list__heading">
          <InteractiveResultWithActionLink
            engine={engine}
            result={result}
            ariaLabel={`${dataset.localization.preview} ${result.raw.documentname}`}
            className={`document-list__btn ${
              result.raw.documentpdfurl
                ? 'document-list__btn--pdf'
                : 'document-list__btn--other'
            }`}
            target="_blank"
            href={result.raw.documentpdfurl}
          >
            <svg
              viewBox="0 0 120 120"
              className="preview-icon"
              width="22"
              height="16"
              role="presentation"
            >
              <use xlinkHref="#eye" />
            </svg>
          </InteractiveResultWithActionLink>
          <p className="document-list__technology">
            {result.raw.documenttechnology}
          </p>
          <h3 className="document-list__name">{result.raw.documentname}</h3>
        </div>
        <ul className="document-list__files">
          {result.raw.documentpdfurl && (
            <DocumentListFile
              docName={result.raw.documentname}
              docTech={result.raw.documenttechnology}
              docType="PDF"
              docUrl={result.raw.documentpdfurl}
              labels={localization}
              engine={engine}
              result={result}
            />
          )}
          {result.raw.documentdwgurl && (
            <DocumentListFile
              docName={result.raw.documentname}
              docTech={result.raw.documenttechnology}
              docType="DWG"
              docUrl={result.raw.documentdwgurl}
              labels={localization}
              engine={engine}
              result={result}
            />
          )}
          {result.raw.documentrvturl && (
            <DocumentListFile
              docName={result.raw.documentname}
              docTech={result.raw.documenttechnology}
              docType="RVT"
              docUrl={result.raw.documentrvturl}
              labels={localization}
              engine={engine}
              result={result}
            />
          )}
          {result.raw.documentdocurl && (
            <DocumentListFile
              docName={result.raw.documentname}
              docTech={result.raw.documenttechnology}
              docType="DOC"
              docUrl={result.raw.documentdocurl}
              labels={localization}
              engine={engine}
              result={result}
            />
          )}
        </ul>
      </li>
    )
  });

  return (
    <ResultActionAnalyticsProvider value={resultActionAnalytics}>
      <DocumentTabListing
        {...props}
        engine={engine}
        resultTemplatesManager={resultTemplatesManager}
      />
    </ResultActionAnalyticsProvider>
  );
}

export default DocumentTabListingContext;
